import React, { FunctionComponent, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';
import { IAppState } from '../../../../common/models/store/IAppState';
import { editContactValidationSchema, reqBuilderStep2ValidationSchema } from '../../../../common/validation/yup';
import Table from '../../../../components/Table';
import AddContactForm from '../../components/AddContactForm';
import { initialFormValues, trimValues } from '../../components/AddContactForm/utils';
import { addRefReqReferenceRoutine, removeRefReqReferenceRoutine, updateRefReqReferenceRoutine } from '../../routines';
import { getSelectedContactsColumns } from '../Step2/utils';
import { usePrevious } from '../../../../common/helpers/customHooks';
import { errorToast } from '../../../../common/helpers/toastrHelper';

interface IProps {
  references: INewContactInfo[];
  addReference: IBindingCallback1<INewContactInfo>;
  removeReference: IBindingCallback1<INewContactInfo>;
  updateReference: IBindingCallback1<{ index: number; data: INewContactInfo; }>;
}

const AddReferencesStep: FunctionComponent<IProps> = ({
  references,
  addReference,
  removeReference,
  updateReference
}) => {
  const [isFormSubmitting, setIsSubmitting] = useState(false);

  const onAddReference = (values: INewContactInfo) => {
    const newRef = trimValues(values);
    addReference(newRef);
  };

  const props = useFormik<INewContactInfo>({
    validationSchema: reqBuilderStep2ValidationSchema,
    initialValues: initialFormValues,
    onSubmit: onAddReference
  });

  const onSubmit = () => {
    setIsSubmitting(true);
    props.submitForm();
  };

  const prevReferences = usePrevious(references);
  useEffect(() => {
    if (!prevReferences || prevReferences.length <= references.length) {
      props.setValues(initialFormValues);
      setIsSubmitting(false);
    }
  }, [references]);

  const onUpdateReference = (index: number, data: INewContactInfo) => {
    try {
      editContactValidationSchema.validateSync(data);
      updateReference({ index, data: trimValues(data) });
    } catch (error) {
      errorToast(error.message);
    }
  };

  return (
    <div className="add-contact-step">
      <header className="add-contact-step-header">
        <FormattedMessage id="addRefStep.description" defaultMessage="Add your candidate’s references here" />
        <AddContactForm
          onSubmit={onSubmit}
          isFormSubmitting={isFormSubmitting}
          buttonText={{
            id: 'candidateForm.addReference',
            defaultMessage: 'Add reference'
          }}
          {...props}
        />
      </header>
      <main className="add-contact-step-body">
        <h1 className="add-contact-step-body-header">
          <FormattedMessage
            id="addRefStep.added"
            defaultMessage="Added references"
            values={{ count: references.length }}
          />
          {Boolean(references.length) && <span className="add-contact-step-count">{references.length}</span>}
        </h1>
        {references.length
          ? (
            <Table
              config={{
                data: { items: references, total: references.length },
                columns: getSelectedContactsColumns({
                  withWarning: false,
                  actions: {
                    onDelete: removeReference,
                    onEdit: onUpdateReference
                  }
                }),
                onCellClicked: (_origin, event) => event.stopPropagation()
              }}
            />
          )
          : (
            <span className="add-contact-step-placeholder">
              <FormattedMessage id="addRefStep.noRefs" defaultMessage="References added will be shown here." />
            </span>
          )}
      </main>
    </div>
  );
};

const mapStateToProps = ({
  requestBuilder: { references }
}: IAppState) => ({ references: references as INewContactInfo[] });

const mapDispatchToProps = {
  addReference: addRefReqReferenceRoutine,
  removeReference: removeRefReqReferenceRoutine,
  updateReference: updateRefReqReferenceRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(AddReferencesStep);
